import { useState, useMemo } from 'react';
import { useI18n } from 'next-localization';

import languageStructure from './changeLanguage';
import useLanguageVersions from 'hooks/useLanguageVersions';
import { Text, NextImage } from '@sitecore-jss/sitecore-jss-nextjs';

import ScLink from 'components/Generic/SitecoreWrappers/ScLink';
import LanguageSelector from 'components/Generic/Header/MegaNavigation/LanguageSelectorMobile';
import MobileMenuSiteSelector from 'components/Generic/Header/MegaNavigation/MobileMenuSiteSelector';
import PromotedContent from 'components/Generic/Header/MegaNavigation/PromotedContent';

import { NavItem } from './MegaNavigation.types';
import icon from 'src/assets/img/custom-svg/artwork.svg';
import { useSitecoreContext } from 'hooks/useSitecoreContext';
import changeSiteStructure from './changeSite.js';

type MegaNavigationBodyMobileProps = {
  categories: Array<NavItem>;
  onCloseMobileMenu: () => void;
  className: string;
};

const MegaNavigationBodyMobile = ({
  categories,
  className,
  onCloseMobileMenu,
}: MegaNavigationBodyMobileProps) => {
  const { t } = useI18n();
  const { sitecoreContext } = useSitecoreContext();
  const languageVersions = useLanguageVersions();

  const [selectedCategory, setSelectedCategory] = useState<NavItem | null>(null);
  const [levels, setLevels] = useState([categories]);
  const [currentDepth, setCurrentDepth] = useState(0);
  const changeLanguageStructure = languageStructure;
  const backLabelString = {
    value: t('back'),
  };

  const sharePrice = sitecoreContext.sharePriceSettings?.sharePrice?.value;
  const sharePriceCta = sitecoreContext.sharePriceSettings?.sharePriceCta;
  const hasSharePrice = !!sharePrice && !!sharePriceCta;

  const pullOutLink = useMemo(() => {
    return selectedCategory?.fields?.pulloutLink?.value?.href
      ? selectedCategory?.fields?.pulloutLink
      : undefined;
  }, [selectedCategory?.fields?.pulloutLink]);
  const currentLevel = useMemo(() => {
    return selectedCategory
      ? selectedCategory?.fields?.items || []
      : !currentDepth
      ? [...categories]
      : categories;
  }, [selectedCategory, categories, currentDepth]);
  const submenuTitle = useMemo(() => {
    return selectedCategory?.fields?.title.value;
  }, [selectedCategory]);
  const isLanguageSelector = useMemo(() => {
    return selectedCategory?.hideTitle;
  }, [selectedCategory]);
  const submenuDescription = useMemo(() => {
    return selectedCategory?.fields?.description?.value;
  }, [selectedCategory]);
  const siteSelectorProperty = useMemo(() => {
    return sitecoreContext.siteSelector;
  }, [sitecoreContext]);
  const promotedBlock = useMemo(() => {
    return selectedCategory?.fields?.promotedContent;
  }, [selectedCategory]);

  const updateBackLabelString = () => {
    backLabelString.value =
      //@ts-expect-error TODO: types are screwed up here
      levels.length > 2 ? levels[levels.length - 2].fields.title.value! : t('back');
  };
  const selectLevel = (level: NavItem) => {
    setCurrentDepth(currentDepth + 1);
    setTimeout(() => {
      //@ts-expect-error TODO: types are screwed up here
      if (!levels.includes(level)) {
        //@ts-expect-error TODO: types are screwed up here
        levels.push(level);
      }
      setSelectedCategory(level);
      updateBackLabelString();
    }, 200);
  };
  const levelBack = () => {
    const targetLevelIdx = levels.length - 2;
    //@ts-expect-error TODO: types are screwed up here
    setSelectedCategory(targetLevelIdx === 0 ? null : levels[targetLevelIdx]);
    levels.pop();
    setCurrentDepth(currentDepth - 1);
    updateBackLabelString();
  };
  const closeMobileMenu = () => {
    setCurrentDepth(0);
    setLevels([categories]);
    setSelectedCategory(null);
    updateBackLabelString();
    onCloseMobileMenu();
  };

  const LinkContent = ({ category }: { category: NavItem }) => {
    if (category.fields?.link) {
      return (
        <ScLink
          field={category.fields?.link}
          onClick={closeMobileMenu}
          className="meganav__menu-item meganav-link meganav-link--small py-3 px-3 ps-sm-4 pe-sm-5 d-inline-flex w-100 revert-underline"
        />
      );
    } else if (!category.hasLinks && category.directLink) {
      return (
        <ScLink
          field={category.directLink}
          onClick={closeMobileMenu}
          className="meganav__menu-item btn btn-naked meganav-link meganav-link--large py-3 px-3 ps-sm-4 pe-sm-5"
        />
      );
    } else if (category.fields?.title) {
      return (
        <button
          onClick={() => selectLevel(category)}
          className="meganav__menu-item btn btn-naked meganav-link meganav-link--large py-3 px-3 ps-sm-4 pe-sm-6"
        >
          {category.fields?.title.value === 'Change Language' && (
            <NextImage
              field={icon}
              className="menu-item-icon me-2"
              alt="Change language"
              width="24"
              height="50"
            />
          )}
          {category.fields?.title.value}
          <i className="bi bi-chevron-right ms-auto" />
        </button>
      );
    }

    if (category.fields?.languageSelector) {
      return <LanguageSelector goBack={levelBack} />;
    }

    if (category.fields?.changeSite) {
      return (
        <MobileMenuSiteSelector
          siteSelectorProperty={siteSelectorProperty}
          changeSiteHandler={closeMobileMenu}
        />
      );
    }
    return null;
  };

  return (
    <div className={`meganav__body__mobile ${className}`}>
      <div
        onClick={onCloseMobileMenu}
        role="button"
        className="overlay__mobile d-none d-sm-block"
        aria-label="Mega navigation overlay"
      />
      <div className="meganav__body__mobile__inner py-4 background-white">
        <div className="meganav__body__mobile__header d-flex align-items-center justify-content-between pb-5 ps-3 pe-2 ps-md-6 pe-md-5">
          {levels.length > 1 && (
            <button
              onClick={levelBack}
              className="btn btn-naked meganav__body__mobile__backlink text-link"
            >
              <i className="bi bi-chevron-left me-2" />
              <Text tag="span" field={backLabelString} />
            </button>
          )}
          <button
            onClick={closeMobileMenu}
            className="btn btn-naked meganav__closer__mobile ms-auto"
            aria-label="Mega menu close button"
          >
            <i className="bi bi-x" aria-hidden={true} />
          </button>
        </div>

        {(submenuTitle || submenuDescription) && !isLanguageSelector && (
          <div className="meganav__body__desc py-3 px-3 ps-sm-4 pe-sm-5">
            {submenuTitle && <p className="section-header-small">{submenuTitle}</p>}
            {submenuDescription && (
              <p className="body-small text-grey mt-2">{submenuDescription}</p>
            )}
          </div>
        )}

        <ul className="meganav__body__mobile__level">
          {currentLevel.map((category, index) => (
            <LinkContent category={category} key={index} />
          ))}
        </ul>
        <div className="overflow-hidden">
          <section>
            {!currentDepth && (
              <div className="meganav__body__mobile__pullout">
                <hr className="mx-2 ms-sm-4 me-sm-5 my-3" />
                <ul className="meganav__body__mobile__level w-100">
                  {siteSelectorProperty && (
                    <li className="d-flex w-100">
                      <button
                        onClick={() => selectLevel(changeSiteStructure as any)}
                        className="meganav__menu-item btn btn-naked meganav-link meganav-link--small py-2 px-2 ps-sm-4 pe-sm-6"
                      >
                        <NextImage
                          field={siteSelectorProperty.changeCountryIcon}
                          width="50"
                          height="24"
                          className="menu-item-icon me-2"
                        />
                        {t('siteSelectorTitle')}
                        <span className="chevron">
                          <i className="bi bi-chevron-right" />
                        </span>
                      </button>
                    </li>
                  )}
                  {languageVersions.length > 1 && (
                    <li className="d-flex w-100">
                      {!currentDepth && (
                        <button
                          onClick={() => selectLevel(changeSiteStructure as any)}
                          className="meganav__menu-item btn btn-naked meganav-link meganav-link--small py-2 px-2 ps-sm-4 pe-sm-5"
                        >
                          {changeLanguageStructure.fields.title.value === 'Change Language' && (
                            <NextImage
                              field={icon}
                              alt={t('changeLanguage')}
                              className="menu-item-icon me-2"
                              width="50"
                              height="24"
                            />
                          )}
                          {t('siteSelectorTitle')}
                          <i className="bi bi-chevron-right" />
                        </button>
                      )}
                    </li>
                  )}
                  <li>
                    {!currentDepth && hasSharePrice && (
                      <ScLink
                        field={sharePriceCta}
                        onClick={closeMobileMenu}
                        replaceWithSlot={true}
                        showExternalIcon={false}
                        className="meganav__menu-item btn btn-naked meganav-link meganav-link--small py-2 px-2 ps-sm-4 pe-sm-6"
                      >
                        <div className="menu-item-icon me-2" />
                        {t('sharePrice')}
                        <span className="chevron">
                          <i className="bi bi-chevron-right" />
                        </span>
                      </ScLink>
                    )}
                  </li>
                </ul>
              </div>
            )}
          </section>
          <section>
            {pullOutLink && (
              <div className="meganav__body__mobile__pullout">
                <hr className="mx-2 my-3" />
                <ScLink
                  field={pullOutLink}
                  replaceWithSlot={true}
                  showExternalIcon={true}
                  onClick={closeMobileMenu}
                  className="meganav-link meganav-link--small py-2 px-2 ps-sm-4 pe-sm-5 d-inline-flex w-100 revert-underline"
                />
              </div>
            )}
          </section>
          {promotedBlock && (
            <div className="meganav__body__mobile__pullout">
              <hr className="mx-2 my-3" />
              <PromotedContent
                key={promotedBlock.id}
                e2eId={promotedBlock.id}
                fields={promotedBlock.fields}
                className="ms-auto promoted-content-spacing"
                isWide={false}
                closeMegaNavBody={closeMobileMenu}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MegaNavigationBodyMobile;
